import { useTranslation } from "react-i18next";
import { Clock } from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";

type PeakHoursCardProps = {
  brokerPeakHours: number[];
  claimPeakHours: number[];
  brokerBlacklisted: number;
  brokerTotal: number;
};

export function PeakHoursCard({ 
  brokerPeakHours, 
  claimPeakHours,
  brokerBlacklisted,
  brokerTotal,
}: PeakHoursCardProps) {
  const { t } = useTranslation();

  const formatHourRange = (hours: number[]) => {
    if (hours.length === 0) return "-";
    const sorted = [...hours].sort((a, b) => a - b);
    return sorted.map(h => `${h}:00`).join(", ");
  };

  // Pie chart data for blacklisted vs normal
  const pieData = [
    { 
      name: t("analytics.peak.normal", "Normal"), 
      value: brokerTotal - brokerBlacklisted,
      color: "#3b82f6"
    },
    { 
      name: t("analytics.peak.blacklisted", "Sonderfälle"), 
      value: brokerBlacklisted,
      color: "#f59e0b"
    },
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700 h-full">
      <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white flex items-center gap-2">
        <Clock className="h-5 w-5 text-blue-500" />
        {t("analytics.peak.title", "Stosszeiten & Sonderfaelle")}
      </h3>
      
      <div className="grid grid-cols-2 gap-4 mb-3">
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
            {t("analytics.peak.broker", "Berater-Abfragen")}
          </p>
          <p className="text-sm font-semibold text-gray-900 dark:text-white">
            {formatHourRange(brokerPeakHours)}
          </p>
        </div>
        
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
            {t("analytics.peak.claim", "Schadenstatus-Abfragen")}
          </p>
          <p className="text-sm font-semibold text-gray-900 dark:text-white">
            {formatHourRange(claimPeakHours)}
          </p>
        </div>
      </div>

      {/* Blacklisted Pie Chart */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          {t("analytics.peak.blacklistedChart", "Sonderfaelle (Berater)")}
        </p>
        
        {brokerTotal > 0 ? (
          <div className="h-32">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={30}
                  outerRadius={45}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: "12px" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-32 flex items-center justify-center text-gray-500 text-sm">
            {t("analytics.noData", "Keine Daten verfuegbar")}
          </div>
        )}
        
        {brokerTotal > 0 && (
          <div className="text-center">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {((brokerBlacklisted / brokerTotal) * 100).toFixed(1)}% {t("analytics.peak.blacklistedRate", "Sonderfaelle")}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
